/**
 * Void a walk-in entered by mistake — the drop-in twin of VoidDialog.
 *
 * A drop-in carries its own money on the row rather than in `payments`, so
 * voiding it needs no negative-payment dance; see the doc comment on
 * `voidWalkIn` for why a soft delete alone is enough here. But the warning to
 * the desk is the same as for a subscription: this is for a session that was
 * never actually taken, not a refund. If the person paid and wants their
 * money back, that is cash leaving the till and belongs in a record of it.
 *
 * Gated on the manager PIN here, at the call site — `voidWalkIn` itself does
 * no authorisation, it is just the write.
 */
import { useState } from 'react';
import { voidWalkIn } from '../db/operations';
import { attemptPin } from '../auth/pin';
import { usePinLock } from '../auth/usePinLock';
import { classLabel } from '@shared/catalog';
import { formatEgp } from '@shared/money';
import { formatDisplayDate } from '@shared/dates';
import type { WalkIn } from '@shared/types';

const PIN_LENGTH = 4;

export default function VoidWalkInDialog({
  walkIn,
  onClose,
}: {
  walkIn: WalkIn;
  onClose: () => void;
}) {
  const [pin, setPin] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { locked, lockText, note } = usePinLock('manager');

  const canSave = pin.length === PIN_LENGTH && !saving && !locked;

  async function save() {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const attempt = await attemptPin(pin, 'manager');
      if (!attempt.ok) {
        setError(note(attempt));
        setPin('');
        setSaving(false);
        return;
      }
      await voidWalkIn(walkIn.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not void');
      setSaving(false);
    }
  }

  return (
    <div
      className="no-print fixed inset-0 z-50 flex items-end justify-center bg-black/80 p-4 sm:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="card max-h-[90vh] w-full max-w-md overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-1 text-2xl text-brand">Void walk-in</h2>
        <p className="mb-5 text-sm text-white/40" dir="auto">
          {walkIn.person_name} · {classLabel(walkIn.class_key)} ·{' '}
          {formatDisplayDate(walkIn.visited_at)} ·{' '}
          {formatEgp(walkIn.amount_piastres, { hideZeroFraction: true })}
        </p>

        <div className="mb-4 rounded-btn border border-edge bg-black/30 p-4 text-sm text-white/60">
          For a drop-in recorded by mistake — wrong class, a duplicate, or
          typed onto the wrong person. This removes the money from today’s
          takings and from the class coach’s share entirely.
        </div>

        <div className="mb-4 rounded-btn border border-status-due/40 bg-status-dueBg p-3 text-sm text-status-due">
          <strong className="block">
            If this person actually paid and wants their money back, this is
            the wrong tool.
          </strong>
          Voiding does not pay anything out — it only erases the record. Cash
          leaving the till needs a record of its own.
        </div>

        <label className="label" htmlFor="voidWalkInPin">
          Manager PIN
        </label>
        <input
          id="voidWalkInPin"
          type="password"
          inputMode="numeric"
          maxLength={PIN_LENGTH}
          value={pin}
          autoFocus
          disabled={saving || locked}
          onChange={(e) => {
            setError('');
            setPin(e.target.value.replace(/\D/g, ''));
          }}
          className="field tabular text-center text-3xl tracking-[0.5em]"
          placeholder="••••"
        />

        {(locked || error) && (
          <p className="mt-4 rounded-btn border border-status-expired/40 bg-status-expiredBg p-3 text-sm text-status-expired">
            {locked ? lockText : error}
          </p>
        )}

        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="btn-ghost flex-1">
            Keep it
          </button>
          <button
            onClick={save}
            disabled={!canSave}
            className="min-h-touch flex-1 rounded-btn bg-status-expired px-4 text-sm font-bold
                       uppercase tracking-button text-paper disabled:opacity-30"
          >
            {saving ? 'Voiding…' : 'Void walk-in'}
          </button>
        </div>

        <p className="mt-3 text-center text-xs leading-relaxed text-white/25">
          This cannot be undone from here. The record is soft-deleted, not
          destroyed.
        </p>
      </div>
    </div>
  );
}
